import React, { useState } from 'react';
import { Keyboard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbApi } from '@/services/ewayBillApi';

const nowIST = () => new Date(Date.now() + 330 * 60e3).toISOString().slice(0, 16);

/** EWB No, generated date & time and valid-until date typed from the GST portal print; `save(body)` returns a promise. */
export const KeyEwbForm = ({ save, onCancel, note, tid = 'ewb-key' }) => {
  const [v, setV] = useState({ ewb_no: '', ewb_date: nowIST(), valid_upto: '' });
  const [busy, setBusy] = useState(false);
  const noOk = /^\d{12}$/.test(v.ewb_no.replace(/\s/g, ''));
  const ok = noOk && v.ewb_date && v.valid_upto && v.valid_upto >= v.ewb_date.slice(0, 10);
  const submit = () => { setBusy(true); save({ ...v, ewb_no: v.ewb_no.replace(/\s/g, '') }).finally(() => setBusy(false)); };
  return (
    <div className="w-full space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid={`${tid}-box`}>
      <p className="text-xs text-gray-600">{note}</p>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1"><Label className="text-xs text-gray-600">EWB No (12 digits)</Label>
          <Input value={v.ewb_no} onChange={(e) => setV({ ...v, ewb_no: e.target.value })} inputMode="numeric" maxLength={14} className="h-9 font-mono" data-testid={`${tid}-no`} /></div>
        <div className="space-y-1"><Label className="text-xs text-gray-600">Generated date & time</Label>
          <Input type="datetime-local" value={v.ewb_date} onChange={(e) => setV({ ...v, ewb_date: e.target.value })} className="h-9" data-testid={`${tid}-date`} /></div>
        <div className="space-y-1"><Label className="text-xs text-gray-600">Valid until (date)</Label>
          <Input type="date" value={v.valid_upto} min={v.ewb_date.slice(0, 10)} onChange={(e) => setV({ ...v, valid_upto: e.target.value })} className="h-9" data-testid={`${tid}-valid`} /></div>
      </div>
      {v.ewb_no && !noOk && <p className="text-xs text-rose-600" data-testid={`${tid}-no-error`}>EWB No must be 12 digits</p>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`${tid}-cancel-btn`}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || !ok} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid={`${tid}-save-btn`}>
          {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save EWB No</Button>
      </div>
    </div>
  );
};

/** Bill made directly on the GST portal: type in the EWB No, generated date & time and valid-until date from the portal print. */
export const KeyEwbBox = ({ b, onChanged }) => {
  const [open, setOpen] = useState(false);
  const save = (body) => ewbApi.keyEwbNo(b.id, body)
    .then((x) => { toast.success(`EWB No ${x.ewb_no} saved — the bill is Active`); setOpen(false); onChanged(x); })
    .catch((e) => toast.error(errMsg(e, 'Could not save the EWB No'), { duration: 8000 }));
  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="ewb-key-open-btn">
        <Keyboard className="w-4 h-4 mr-1" />Type in EWB No (made on the portal)
      </Button>
    );
  }
  return <KeyEwbForm save={save} onCancel={() => setOpen(false)}
    note="Made this e-way bill directly on the GST portal? Type the details from the portal print — the bill turns Active." />;
};

export default KeyEwbBox;
